// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { StargateHealthChecker } from '../utils/healthcheck';

const StargateHealthContext = createContext(null);

export const useStargateHealth = () => {
  const context = useContext(StargateHealthContext);
  if (!context) {
    throw new Error('useStargateHealth must be used within StargateHealthProvider');
  }
  return context;
};

export const StargateHealthProvider = ({ children, ip, apiKey }) => {
  const [healthStatus, setHealthStatus] = useState({
    healthy: null, // null = not checked yet, true = healthy, false = unhealthy
    checking: true, // Start with checking = true to prevent rendering before first check
    error: null,
    hasCheckedOnce: false, // Track if we've completed at least one check
    initialized: false, // Additional flag to prevent any rendering until we're ready
  });

  const healthCheckerRef = useRef(null);
  const mountedRef = useRef(true);
  // Consecutive failed checks while previously healthy. A single transient
  // failure must NOT unmount the whole widget stack (it makes the page jump),
  // so we only flip healthy -> unhealthy after 2 consecutive failures.
  const failStreakRef = useRef(0);

  /** Apply a health-check result with hysteresis and without no-op re-renders. */
  const applyHealthResult = (result) => {
    if (!mountedRef.current) return;
    setHealthStatus((prev) => {
      let healthy = result.healthy;
      if (result.healthy === true) {
        failStreakRef.current = 0;
      } else if (prev.healthy === true) {
        failStreakRef.current += 1;
        if (failStreakRef.current < 2) {
          healthy = true; // tolerate one transient failure
        }
      }

      const nextError = healthy ? null : result.error || null;
      // Skip the state update entirely when nothing material changed —
      // otherwise every periodic check re-renders every widget below us.
      if (
        prev.healthy === healthy &&
        prev.checking === false &&
        prev.hasCheckedOnce === true &&
        prev.initialized === true &&
        (prev.error?.message || null) === (nextError?.message || null)
      ) {
        return prev;
      }
      return {
        healthy,
        checking: false,
        error: nextError,
        hasCheckedOnce: true,
        initialized: true,
      };
    });
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!ip || !apiKey) {
      // Missing IP or API key
      setHealthStatus({
        healthy: false,
        checking: false,
        error: new Error('Missing IP or API key'),
        hasCheckedOnce: true,
      });
      return;
    }

    // Initializing health check

    // Create or update health checker
    if (
      !healthCheckerRef.current ||
      healthCheckerRef.current.ip !== ip ||
      healthCheckerRef.current.apiKey !== apiKey
    ) {
      healthCheckerRef.current = new StargateHealthChecker(ip, apiKey);
    }

    // Perform initial health check
    const checkHealth = async () => {
      // Starting health check
      setHealthStatus((prev) => ({
        ...prev,
        checking: true,
        initialized: false,
      }));

      // Small delay to ensure state updates properly
      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        const result = await healthCheckerRef.current.getHealthStatus();
        applyHealthResult(result);
      } catch (error) {
        applyHealthResult({
          healthy: false,
          error: {
            message: 'Failed to check Stargate connectivity',
            type: 'unknown',
            originalError: error,
          },
        });
      }
    };

    // Set up periodic health checks with exponential backoff
    let checkTimeoutId = null;

    const scheduleNextCheck = () => {
      if (!mountedRef.current || !healthCheckerRef.current) return;

      // Clear any existing timeout
      if (checkTimeoutId) {
        clearTimeout(checkTimeoutId);
      }

      // Get the dynamic interval from the health checker (with exponential backoff)
      const dynamicInterval = healthCheckerRef.current.getDynamicInterval
        ? healthCheckerRef.current.getDynamicInterval()
        : 30000;

      // Schedule next health check

      checkTimeoutId = setTimeout(() => {
        if (mountedRef.current && healthCheckerRef.current) {
          healthCheckerRef.current
            .getHealthStatus()
            .then((result) => {
              if (mountedRef.current) {
                applyHealthResult(result);
                // Schedule the next check
                scheduleNextCheck();
              }
            })
            .catch(() => {
              // Ignore errors in periodic check
              // Schedule next check anyway
              scheduleNextCheck();
            });
        }
      }, dynamicInterval);
    };

    // Start scheduling checks after initial check
    checkHealth()
      .then(() => {
        scheduleNextCheck();
      })
      .catch(() => {
        scheduleNextCheck();
      });

    return () => {
      if (checkTimeoutId) {
        clearTimeout(checkTimeoutId);
      }
    };
  }, [ip, apiKey]);

  const refreshHealth = async () => {
    if (healthCheckerRef.current) {
      healthCheckerRef.current.clearCache();

      setHealthStatus((prev) => ({ ...prev, checking: true }));
      // Manual refresh should reflect reality immediately — drop hysteresis credit.
      failStreakRef.current = 1;

      try {
        const result = await healthCheckerRef.current.getHealthStatus();
        applyHealthResult(result);
      } catch (error) {
        applyHealthResult({
          healthy: false,
          error: {
            message: 'Failed to check Stargate connectivity',
            type: 'unknown',
            originalError: error,
          },
        });
      }
    }
  };

  const value = useMemo(
    () => ({
      ...healthStatus,
      refreshHealth,
    }),
    [healthStatus, refreshHealth]
  );

  return <StargateHealthContext.Provider value={value}>{children}</StargateHealthContext.Provider>;
};
